import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import AccountDetails from "../Components/AccountDetails";
import BhagavatamDonationModal from "../Components/BhagavatamDonationModal";
import srimadBhagavatamImg from "../assets/srimad_bhagavatam_set.png";

// Seva Scroller Images
import sevaBhagavadGita from "../assets/seva_bhagavad_gita.png";
import sevaAnnadana from "../assets/seva_annadana.png";
import sevaGau from "../assets/seva_gauseva.png";
import sevaPitruPaksha from "../assets/seva_pitrupaksha.png";

function SrimadBhagavatam() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [quantity, setQuantity] = useState(2);
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);

  // Grouped exactly as in the reference image
  const row1Languages = [
    "English",
    "Kannada",
    "Hindi",
    "Telugu",
    "Tamil",
    "Marathi",
    "Odia",
  ];

  const row2Languages = ["Bengali", "Malayalam", "English 44 Volume"];

  const priceDetails = {
    English: { current: "Rs. 8,600.00", original: "Rs. 10,200.00" },
    Kannada: { current: "Rs. 5,900.00", original: "Rs. 7,500.00" },
    Hindi: { current: "Rs. 6,600.00", original: "Rs. 8,300.00" },
    Telugu: { current: "Rs. 6,200.00", original: "Rs. 7,500.00" },
    Tamil: { current: "Rs. 7,200.00", original: "Rs. 8,700.00" },
    Marathi: { current: "Rs. 6,800.00", original: "Rs. 7,500.00" },
    Odia: { current: "Rs. 7,400.00", original: "Rs. 9,000.00" },
    Bengali: { current: "Rs. 6,600.00", original: "Rs. 8,200.00" },
    Malayalam: { current: "Rs. 10,500.00", original: "Rs. 13,000.00" },
    "English 44 Volume": { current: "Rs. 10,200.00" },
  };

  const currentPrice =
    priceDetails[selectedLanguage]?.current || "Rs. 8,600.00";
  const originalPrice =
    priceDetails[selectedLanguage]?.original || "Rs. 10,200.00";

  const handleDecrement = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handleIncrement = () => {
    setQuantity((prev) => prev + 1);
  };

  // Sevas list redirecting to their respective pages
  const relatedSevas = [
    {
      id: "gita-daan",
      title: "DONATE BHAGAVAD GITA",

      image: sevaBhagavadGita,
      link: "/gita-daan",
    },
    {
      id: "annadana",
      title: "Annadana Seva ",

      image: sevaAnnadana,
      link: "/dasaraAnnadan",
    },
    {
      id: "gau-seva",
      title: "Gau Seva",
      image: sevaGau,
      link: "/gauseva",
    },
    {
      id: "pitru-paksha",
      title: "Pitru Paksha Seva",

      image: sevaPitruPaksha,
      link: "/pitruPakshaSeva2026",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Srimad Bhagavatam (18/44 Volumes) | ISKCON Vijayawada</title>
        <meta
          name="description"
          content="Srimad-Bhagavatam is the most complete and authoritative exposition of Vedic knowledge-covering everything from the nature of the self to the origin of the universe."
        />
      </Helmet>
      <Header />
      <main className="bg-white py-10 md:py-16">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            {/* Left Column: Book Set Image (Sticky / Fixed on Desktop) */}
            <div className="lg:col-span-6">
              <div className="lg:sticky lg:top-36 flex justify-center lg:justify-end">
                <div className="w-full max-w-[520px]">
                  <img
                    src={srimadBhagavatamImg}
                    alt="Srimad Bhagavatam (18/44 Volumes)"
                    className="w-full h-auto object-contain block select-none"
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Details & Actions */}
            <div className="lg:col-span-6 flex flex-col justify-start">
              {/* Product Title */}
              <h1 className="font-garamond text-3xl sm:text-4xl lg:text-[40px] font-normal text-[#2b2b2b] leading-[1.22] mb-5 tracking-tight">
                Srimad Bhagavatam (18/44
                <br />
                Volumes)
              </h1>

              {/* Language Display */}
              <div className="text-[#444444] text-[15px] sm:text-base font-normal mb-3">
                Language: {selectedLanguage}
              </div>

              {/* Language Selector Badges matching rows in image */}
              <div className="flex flex-col gap-2 mb-6">
                {/* Row 1 */}
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  {row1Languages.map((lang) => {
                    const isSelected = selectedLanguage === lang;
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => setSelectedLanguage(lang)}
                        className={`px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded transition-all duration-150 cursor-pointer ${
                          isSelected
                            ? "bg-[#222222] text-white border border-[#222222] font-medium shadow-sm"
                            : "bg-white text-gray-700 border border-gray-300 hover:border-gray-500 font-normal"
                        }`}
                      >
                        {lang}
                      </button>
                    );
                  })}
                </div>

                {/* Row 2 */}
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  {row2Languages.map((lang) => {
                    const isSelected = selectedLanguage === lang;
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => setSelectedLanguage(lang)}
                        className={`px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded transition-all duration-150 cursor-pointer ${
                          isSelected
                            ? "bg-[#222222] text-white border border-[#222222] font-medium shadow-sm"
                            : "bg-white text-gray-700 border border-gray-300 hover:border-gray-500 font-normal"
                        }`}
                      >
                        {lang}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Price Section */}
              <div className="flex items-baseline gap-2.5 mb-6">
                <span className="text-[#ea583a] text-2xl sm:text-[28px] font-medium tracking-tight">
                  {currentPrice}
                </span>
                <span className="text-gray-400 line-through text-sm sm:text-base font-normal">
                  {originalPrice}
                </span>
              </div>

              {/* Quantity Section */}
              <div className="mb-2">
                <div className="text-gray-800 text-[15px] sm:text-base font-normal mb-2.5">
                  Quantity
                </div>

                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Quantity Counter Box */}
                  <div className="flex items-center justify-between border border-gray-300 rounded h-11 w-28 sm:w-32 px-1 bg-white">
                    <button
                      type="button"
                      onClick={handleDecrement}
                      className="w-8 h-full flex items-center justify-center text-gray-500 hover:text-black text-lg transition-colors cursor-pointer select-none"
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span className="text-gray-800 font-medium text-base text-center w-8 select-none">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={handleIncrement}
                      className="w-8 h-full flex items-center justify-center text-gray-500 hover:text-black text-lg transition-colors cursor-pointer select-none"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>

                  {/* Donate Now Button */}
                  <button
                    type="button"
                    onClick={() => setIsDonateModalOpen(true)}
                    className="flex-1 max-w-[280px] sm:max-w-[320px] h-11 px-8 sm:px-12 bg-[#ea583a] hover:bg-[#d8482b] active:scale-[0.99] text-white text-base font-medium rounded transition-colors duration-150 flex items-center justify-center shadow-sm cursor-pointer whitespace-nowrap"
                  >
                    Donate now
                  </button>
                </div>
              </div>


              {/* Srimad Bhagavatam Exposition Description */}
              <div className="mt-8 space-y-4 text-[#555555] text-sm sm:text-[15px] leading-relaxed font-normal">
                <p>
                  Srimad-Bhagavatam is the most complete and authoritative
                  exposition of Vedic knowledge-covering everything from the
                  nature of the self to the origin of the universe.
                </p>
                <p>
                  The first verse of Srimad-Bhagavatam makes clear that because
                  the book is intended for people serious about spiritual
                  progress, it will not deal with sectarian religious ideas,
                  philosophical conjecture, or worldly concerns.
                </p>
                <p>
                  The second text promises that anyone who reads the book
                  systematically will achieve the spiritual success meant for
                  all human beings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Related Sevas Section: "You Might Also Like" - Fixed 4 Cards */}
      <div className="bg-white py-12 md:py-16 border-t border-gray-100">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Heading */}
          <h2 className="font-garamond text-3xl sm:text-4xl text-[#2b2b2b] font-normal tracking-tight text-center mb-8 sm:mb-10">
            You Might Also Like
          </h2>

          {/* 4 Cards Grid - Non-scrollable */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {relatedSevas.map((seva) => (
              <Link
                key={seva.id}
                to={seva.link}
                onClick={() => window.scrollTo(0, 0)}
                className="group cursor-pointer no-underline text-inherit block transition-transform duration-200 hover:-translate-y-1"
              >
                {/* Card Image */}
                <div className="w-full aspect-[3/4] rounded-xl overflow-hidden bg-gray-50 shadow-sm border border-gray-100 flex items-center justify-center">
                  <img
                    src={seva.image}
                    alt={seva.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 select-none"
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bhagavatam Donation Checkout Modal Popup */}
      <BhagavatamDonationModal
        isOpen={isDonateModalOpen}
        onClose={() => setIsDonateModalOpen(false)}
        selectedLanguage={selectedLanguage}
        quantity={quantity}
        setQuantity={setQuantity}
        currentPrice={currentPrice}
        originalPrice={originalPrice}
        image={srimadBhagavatamImg}
      />

      <Footer />
    </>
  );
}

export default SrimadBhagavatam;
